import { FaFilePdf } from "react-icons/fa"; 
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import styles from "./Firmarcert.module.css";
import { postArchivo, postSavefirm, getCertificado } from "../../../redux/actions";
import PdfSelloViewer from "./PdfSelloViewer";
import SelloManager from "./SelloManager";

const FirmarPDF = () => {
  const dispatch = useDispatch();
  const userNombre = sessionStorage.getItem("userNombre") || "";
  const baseURL = "https://verificacion.incolabcolombia.com.co";

  const [file, setFile] = useState(null);
  const [referencia, setReferencia] = useState("");
  const [cliente, setCliente] = useState("");
  const [errorMensaje, setErrorMensaje] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Estado para el visor de sello
  const [showSelloViewer, setShowSelloViewer] = useState(false);
  const [selloCoords, setSelloCoords] = useState(null);
  const [showSelloManager, setShowSelloManager] = useState(false);
  const itemsPerPage = 10;

  const cert = useSelector((state) => state.certificado);
  const firmas = cert.firmas || [];
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(getCertificado());
  }, [dispatch]);

  // 🔹 Abre el visor de posicionamiento de sello
  const handleAbrirVisorSello = (e) => {
    e.preventDefault();
    if (!referencia.trim() || !cliente.trim() || !file) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        text: "Por favor completa Referencia, Cliente y selecciona un PDF antes de continuar.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }
    setShowSelloViewer(true);
  };

  // 🔹 Cuando el usuario confirma posición del sello
  const handleSelloConfirmado = async (coords) => {
    setShowSelloViewer(false);
    setSelloCoords(coords);
    setLoading(true);

    try {
      // Enviar al backend: el PDF + los datos del sello como FormData
      const firmado = await dispatch(postArchivo(file, coords));
      const saveData = {
        ruta: firmado.urlDocumento,
        fecha: firmado.fechaFirmado,
        Usercrea: userNombre,
        Ref: referencia,
        Cliente: cliente,
      };
      await dispatch(postSavefirm(saveData));
      Swal.fire({
        icon: "success",
        title: "PDF firmado y guardado correctamente",
        showConfirmButton: false,
        timer: 2500,
      });
      setFile(null);
      setReferencia("");
      setCliente("");
      setErrorMensaje("");
      setSelloCoords(null);
      if (fileInputRef.current) fileInputRef.current.value = null;
      dispatch(getCertificado());
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.mensaje || err.message || "Ocurrió un error inesperado",
      });
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Descarga automática
  const handleDownload = async (ruta, ref) => {
    try {
      const response = await fetch(`https://verificacion.incolabcolombia.com.co${ruta}`);
      if (!response.ok) throw new Error("No se pudo descargar el archivo");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ref}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error al descargar el archivo:", err);
    }
  };

  // 🔹 Filtrado de búsqueda
  const filteredFirmas = firmas.filter(
    (f) =>
      f.Cliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.Ref.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 🔹 Paginación
  const totalPages = Math.ceil(filteredFirmas.length / itemsPerPage);
  const displayedFirmas = filteredFirmas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 🔹 Generar botones de paginación tipo 1-10 ...
  const renderPagination = () => {
    const pages = [];
    const maxButtons = 10;
    let start = Math.floor((currentPage - 1) / maxButtons) * maxButtons + 1;
    let end = Math.min(start + maxButtons - 1, totalPages);
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`${styles.button} ${currentPage === i ? styles.activePage : ""}`}
          style={{ margin: "2px", padding: "5px 10px", fontSize: "0.8rem" }}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }
    if (end < totalPages) pages.push(<span key="dots">...</span>);
    return pages;
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <h2 className={styles.title}>Firma de Certificados</h2>

        {/* Botón gestionar sellos */}
        <div style={{ textAlign: "right", marginBottom: "14px" }}>
          <button
            type="button"
            className={styles.button}
            style={{
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              fontSize: "0.85rem",
              padding: "8px 16px",
            }}
            onClick={() => setShowSelloManager(true)}
          >
            🖋️ Gestionar Sellos
          </button>
        </div>

        <form onSubmit={handleAbrirVisorSello} className={styles.formHorizontal}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Referencia</label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej: REF-2025-001"
              className={styles.input}
            />
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>Cliente</label>
            <input
              type="text"
              value={cliente}
              onChange={(e) => setCliente(e.target.value)}
              placeholder="Ej: Incolab Services"
              className={styles.input}
            />
          </div>

          <div className={styles.fileBox}>
            <label htmlFor="pdfUpload" className={styles.fileLabel}>
              Seleccionar PDF
            </label>
            <input
              id="pdfUpload"
              type="file"
              accept="application/pdf"
              onChange={(e) => setFile(e.target.files[0])}
              ref={fileInputRef}
              className={styles.fileInput}
            />
            {file && <span className={styles.fileName}>{file.name}</span>}
          </div>

          <button
            type="submit"
            className={styles.button}
            disabled={loading || !file || errorMensaje !== ""}
          >
            {loading ? "Firmando..." : "Firmar PDF"}
          </button>
        </form>

        {errorMensaje && <p className={styles.error}>{errorMensaje}</p>}

        {/* Botón para abrir modal de certificados */}
        {firmas.length > 0 && (
          <button
            className={styles.button}
            style={{ marginTop: "20px", background: "#1e3a8a" }}
            onClick={() => setModalOpen(true)}
          >
            Ver Certificados Firmados ({firmas.length})
          </button>
        )}
      </div>

      {/* ===== VISOR DE SELLO / FIRMA ===== */}
      {showSelloViewer && (
        <PdfSelloViewer
          pdfFile={file}
          onConfirm={handleSelloConfirmado}
          onCancel={() => setShowSelloViewer(false)}
        />
      )}

      {/* ===== GESTOR DE SELLOS GUARDADOS ===== */}
      {showSelloManager && (
        <SelloManager onClose={() => setShowSelloManager(false)} />
      )}

      {/* Modal de certificados firmados */}
      {modalOpen && (
        <div className={styles.modalBackdrop} onClick={() => setModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3>Certificados Firmados</h3>

            {/* Input de búsqueda */}
            <input
              type="text"
              placeholder="Buscar por Cliente o Referencia..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className={styles.input}
              style={{ marginBottom: "10px" }}
            />

            {/* Tabla */}
            <table className={styles.tabla}>
              <thead>
                <tr>
                  <th>Referencia</th>
                  <th>Cliente</th>
                  <th>Fecha</th>
                  <th>Creado por</th>
                  <th style={{ textAlign: "center" }}>Certificado</th>
                </tr>
              </thead>
              <tbody>
                {displayedFirmas.map((f) => (
                  <tr key={f.id}>
                    <td>{f.Ref}</td>
                    <td>{f.Cliente}</td>
                    <td>{new Date(f.fecha).toLocaleString()}</td>
                    <td>{f.Usercrea}</td>
                    <td style={{ textAlign: "center" }}>
                      <FaFilePdf
                        size={22}
                        color="#E63946"
                        className={styles.pdfIcon}
                        title="Descargar PDF"
                        onClick={() => handleDownload(f.ruta, f.Ref)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            <div style={{ marginTop: "15px", textAlign: "center" }}>
              {renderPagination()}
            </div>

            <button
              className={styles.button}
              style={{ marginTop: "15px", background: "#dc2626" }}
              onClick={() => setModalOpen(false)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FirmarPDF;
